// @flow
import React from 'react';
import { connect } from 'react-redux';
import classnames from 'classnames';
import Button from '@localytics/loc-components/lib/Button';
import Tooltip from '@localytics/loc-components/lib/Tooltip';
import { get as _get } from 'lodash';
import DurationPicker from './DurationPicker';
import DeleteNodeModal from './DeleteNodeModal';
import ConfirmationDialog from './ConfirmationDialog';
import EventSelectionForm from './EventAttributes/EventSelectionForm';
import { StandardNode } from './StandardNode';
import { typeFromOperator } from '../util/AttributeUtils';
import { startDeletingNode, stopDeletingNode } from '../actions/tree';
import { EVENT_LISTENER_TYPE, ADD_NODE_TYPE } from '../constants/ApiTypes';
import './stylesheets/TempButton.scss';

import {
  EVENT_LISTENER_FILL_COLOR,
  EVENT_LISTENER_ICON
} from '../constants/Style';
import {
  bucketMinForDisplay,
  minutesInAnHour,
  minutesInAMonth,
  HOUR
} from '../util/TimeUtils';
import './stylesheets/EventNodeModal.scss';

import type {
  Node,
  AttributeCondition,
  OperatorOptionsType
} from '../constants/ApiTypes';
import type { EventMapping, AttributeGroup } from '../constants/RailsApiTypes';

type EventNodeState = {
  expirationMinutes: number,
  unit: string,
  hasValidDuration: boolean,
  showConfirmationDialog: boolean,
  selectedEvent: ?EventMapping,
  selectedAttribute: ?AttributeGroup,
  selectedOperator: ?OperatorOptionsType,
  selectedAttributeValues: Array<string>
};

type EventNodeModalProps = {
  nodeExistsInFlow: boolean,
  expiration_minutes: number,
  unit: string,
  event_mapping_id: number,
  attribute_group_id: number,
  height: number,
  rx: number,
  ry: number,
  parentName: string,
  submitFormCallback: Function,
  cancelCallback?: Function,
  closeCallback: Function,
  parentNodeName: string,
  editable: boolean,
  deletingNode: boolean,
  dispatch: Function,
  node: Node,
  appIds: Array<Object>,
  eventMappings: Array<EventMapping>,
  currentOrgId: string
};

const MINIMUM_DURATION_IN_MIN = minutesInAnHour;
const MAXIMUM_DURATION_IN_MIN = minutesInAMonth; // 31 day max

class EventNodeModal extends React.Component<
  EventNodeModalProps,
  EventNodeState
> {
  constructor(props: EventNodeModalProps) {
    super(props);
    const {
      eventMappings,
      expiration_minutes: expirationMinutes,
      unit,
      event_mapping_id: eventMappingId,
      attribute_group_id: attributeGroupId,
      node
    } = this.props;

    const selectedEvent = eventMappings.find(
      mapping => mapping.id === eventMappingId
    );
    const selectedAttribute = _get(selectedEvent, 'attribute_groups', []).find(
      attr => attr.id === attributeGroupId
    );
    const conditions = _get(node, 'data.events.0.attributes_condition');
    const selectedOperator = _get(conditions, 'op');
    const value = _get(conditions, 'value');
    const values = _get(conditions, 'values');
    const selectedAttributeValues = values || [value].filter(v => !!v);
    this.isDirty = false;
    this.state = {
      expirationMinutes: expirationMinutes || minutesInAnHour,
      unit: unit || HOUR,
      hasValidDuration: true,
      showConfirmationDialog: false,
      selectedEvent,
      selectedAttribute,
      selectedOperator,
      selectedAttributeValues
    };
  }

  componentWillUpdate() {
    this.isDirty = true;
  }

  onEventUpdate: Function = selectedEvent =>
    this.setState({ selectedEvent, selectedAttribute: null });
  onAttributeUpdate: Function = selectedAttribute =>
    this.setState({
      selectedAttribute,
      selectedOperator: null,
      selectedAttributeValues: []
    });
  onOperatorUpdate: Function = selectedOperator =>
    this.setState({ selectedOperator, selectedAttributeValues: [] });
  onAttributeValuesUpdate: Function = selectedAttributeValues =>
    this.setState({ selectedAttributeValues });

  getAttributeConditions: Function = olapId => {
    const {
      selectedAttribute,
      selectedOperator,
      selectedAttributeValues
    } = this.state;
    if (!selectedAttribute) {
      return null;
    }
    const attributeMapping = selectedAttribute.attribute_mappings.find(
      m => m.olap_id === olapId
    );
    if (!attributeMapping) {
      return null;
    }
    const conditions: AttributeCondition = {
      property: attributeMapping.shorthand,
      op: _get(selectedOperator, 'id', 'EQ'),
      type: typeFromOperator(_get(selectedOperator, 'id', 'EQ'))
    };
    if (selectedOperator && selectedOperator.multiple) {
      conditions.values = selectedAttributeValues;
    } else {
      const value = _get(selectedAttributeValues, '0');
      conditions.value = value;
    }
    return conditions;
  };

  getEventsForNode: Function = () => {
    const { selectedEvent, selectedAttribute } = this.state;

    if (!selectedEvent) {
      return [];
    }
    if (!selectedAttribute) {
      return selectedEvent.events.map(e => ({
        name: e.name,
        olap_id: e.olap_id
      }));
    }

    const eventsWithAttributes = selectedEvent.events.map(e => ({
      name: e.name,
      olap_id: e.olap_id,
      attributes_condition: this.getAttributeConditions(e.olap_id)
    }));

    return eventsWithAttributes.filter(e => e.attributes_condition);
  };

  isDirty: boolean;

  submitForm: Function = () => {
    const {
      expirationMinutes,
      unit,
      hasValidDuration,
      selectedEvent,
      selectedAttribute
    } = this.state;
    if (hasValidDuration && selectedEvent) {
      const events = this.getEventsForNode();
      this.props.submitFormCallback({
        display_name: selectedEvent.name,
        event_mapping_id: selectedEvent.id,
        attribute_group_id: _get(selectedAttribute, 'id', null),
        events,
        expiration_minutes: expirationMinutes,
        unit,
        node_type: EVENT_LISTENER_TYPE
      });
    }
  };

  close: Function = () => {
    if (this.props.editable && this.isDirty) {
      this.setState({
        showConfirmationDialog: true
      });
    } else {
      this.closeForReal();
    }
  };

  closeForReal: Function = () => {
    this.props.closeCallback();
  };

  cancel: Function = () => {
    (this.props.cancelCallback
      ? this.props.cancelCallback
      : this.props.closeCallback)();
  };

  updateExpirationMinutes: Function = ({ minutes, unit }) => {
    if (
      minutes >= MINIMUM_DURATION_IN_MIN &&
      minutes <= MAXIMUM_DURATION_IN_MIN
    ) {
      this.setState({
        expirationMinutes: minutes,
        unit: unit || this.state.unit,
        hasValidDuration: true
      });
    } else {
      this.setState({ hasValidDuration: false });
    }
  };

  toggleShowDeleteModal: Function = () => {
    const { deletingNode, dispatch } = this.props;
    if (deletingNode) {
      dispatch(stopDeletingNode());
    } else {
      dispatch(startDeletingNode());
    }
  };

  hideConfirmationDialog: Function = () => {
    this.setState({ showConfirmationDialog: false });
  };

  focusEventDropdown: Function = div => {
    if (div) {
      div.getElementsByClassName('Select-input')[0].focus();
    }
  };

  render() {
    const {
      appIds,
      nodeExistsInFlow,
      expiration_minutes: expirationMinutes,
      unit,
      height,
      rx,
      ry,
      parentName,
      parentNodeName,
      editable,
      deletingNode,
      eventMappings
    } = this.props;
    const {
      hasValidDuration,
      selectedEvent,
      selectedAttribute,
      selectedOperator,
      selectedAttributeValues
    } = this.state;

    const eventApps = eventMappings.filter(em =>
      appIds.every(appId => em.events.map(ev => ev.app_id).includes(appId))
    );

    if (deletingNode) {
      return (
        <DeleteNodeModal
          cancelModal={this.toggleShowDeleteModal}
          closeModal={this.close}
        />
      );
    }
    return (
      <div>
        <div className="EventNodeModal__Title">
          <svg
            style={{
              height: '60px',
              width: '100%'
            }}
          >
            <StandardNode
              height={height}
              rx={rx}
              ry={ry}
              icon={EVENT_LISTENER_ICON}
              iconFill={EVENT_LISTENER_FILL_COLOR}
              botLeftRounded={false}
            >
              <text
                fill="#3F404A"
                y="12"
                style={{
                  fontFamily: 'proxima-nova',
                  fontWeight: '300',
                  fontSize: '24px'
                }}
              >
                {nodeExistsInFlow
                  ? `${editable ? 'Edit ' : ''}Event Listener`
                  : `Add Event Listener`}
              </text>
            </StandardNode>
          </svg>
          <button
            className="EventNodeModal__CloseButton"
            onClick={this.close}
            tabIndex="-1"
          >
            &times;
          </button>
        </div>
        <div className="EventNodeModal__Content">
          <div className="EventNodeModal__MainText">
            {editable
              ? `If a user performs the following event and matches the \
              criteria specified below within a given time frame, they will \
              proceed down the “did” path. Otherwise, they will proceed down \
              the “did not” path.`
              : ''}
          </div>
          <div
            className="EventNodeModal__FormContainer"
            ref={this.focusEventDropdown}
          >
            <EventSelectionForm
              eventMappings={eventApps}
              onEventUpdate={this.onEventUpdate}
              onAttributeUpdate={this.onAttributeUpdate}
              onOperatorUpdate={this.onOperatorUpdate}
              onAttributeValuesUpdate={this.onAttributeValuesUpdate}
              selectedEvent={selectedEvent}
              selectedAttribute={selectedAttribute}
              selectedOperator={selectedOperator}
              selectedAttributeValues={selectedAttributeValues}
              currentOrgId={this.props.currentOrgId}
              disabled={!editable}
            />

            <div className="EventNodeModal__FormRow EventNodeModal__FormRow--Field">
              <div className="EventNodeModal__FormLeft">
                <span
                  style={{
                    color: '#3F404A',
                    fontWeight: '600',
                    display: 'inline-block'
                  }}
                >
                  Within
                </span>
              </div>

              <div className="EventNodeModal__FormRight">
                <div className="EventNodeModal__InputsContainer">
                  <DurationPicker
                    updateTime={this.updateExpirationMinutes}
                    minutes={expirationMinutes}
                    unit={unit}
                    disabled={!editable}
                  />
                  <span
                    style={{
                      fontWeight: '600',
                      display: 'inline-block',
                      padding: '0.5rem'
                    }}
                  >
                    of
                  </span>
                  <span
                    style={{
                      color: EVENT_LISTENER_FILL_COLOR,
                      fontWeight: '600',
                      display: 'inline-block',
                      textTransform: 'capitalize'
                    }}
                  >
                    {`${parentNodeName || parentName}.`}
                  </span>
                </div>
              </div>
            </div>
          </div>
          {nodeExistsInFlow && (
            <button
              className={classnames('EventNodeModal__DeleteNodeButton', {
                hidden: !editable
              })}
              tabIndex={-1}
              onClick={this.toggleShowDeleteModal}
            >
              Delete this event listener
            </button>
          )}
        </div>

        <div
          className={classnames('EventNodeModal__ControlButtons', {
            hidden: !editable
          })}
        >
          <div>
            <Button tabIndex={-1} onClick={this.cancel} className="TempButton">
              Cancel
            </Button>
            {!hasValidDuration || !selectedEvent ? (
              <Tooltip
                content={`${
                  !hasValidDuration
                    ? `Please select a duration between ${bucketMinForDisplay(
                        MINIMUM_DURATION_IN_MIN
                      )} and ${bucketMinForDisplay(MAXIMUM_DURATION_IN_MIN)}`
                    : ''
                }${!selectedEvent ? `Please select a valid event` : ''}`}
              >
                <Button
                  tabIndex={-1}
                  type="primary"
                  onClick={this.submitForm}
                  className="TempPrimaryButton"
                  disabled
                >
                  {nodeExistsInFlow ? 'Update' : 'Add'}
                </Button>
              </Tooltip>
            ) : (
              <Button
                tabIndex={-1}
                type="primary"
                onClick={this.submitForm}
                className="TempPrimaryButton"
              >
                {nodeExistsInFlow ? 'Update' : 'Add'}
              </Button>
            )}
          </div>
        </div>
        <ConfirmationDialog
          showConfirmationDialog={this.state.showConfirmationDialog}
          onCancel={this.hideConfirmationDialog}
          onDontSave={this.closeForReal}
          onSave={this.submitForm}
          nodeTitle="the Event Listener"
        />
      </div>
    );
  }
}

const mapStateToProp = (state, ownProps) => {
  const {
    railsApi: { eventMappings },
    tree: { selectedNode, nodesList, deletingNode },
    api: { versionsByOrchestrationId }
  } = state;
  const node = nodesList.find(n => n.data.name === selectedNode);
  const nodeExistsInFlow = node && node.data.node_type !== ADD_NODE_TYPE;
  const {
    [ownProps.currentVersionId]: version = {}
  } = versionsByOrchestrationId[ownProps.currentOrchestrationId];
  const { nodes: { [ownProps.parentName]: parent = {} } = {} } = version;
  const appIds = version.apps ? version.apps.map(a => a.app_uuid) : [];
  return {
    attribute_group_id: _get(node, 'data.attribute_group_id', null),
    appIds,
    eventMappings,
    nodeExistsInFlow,
    parentNodeName: parent.display_name || parent.node_type,
    deletingNode,
    node
  };
};

export default connect(mapStateToProp)(EventNodeModal);



// WEBPACK FOOTER //
// ./src/components/EventNodeModal.jsx